
import { useEffect, useState } from "react";
import {
  customersAPI,
  measurementAPI,
  getCmId,
} from "../../services/api";

import { useRouter } from "../../contexts/RouterContext";
import { Breadcrumb, Loading, Spinner } from "../../components/ui";
import { fmt } from "../../utils/helpers";
import MeasurementModal from "./MeasurementModal";

const MEASUREMENT_LIMIT = 12;

// Measurement labels are taken from Get Measurement Details API
const getMeasurementLabels = (data) => {
  if (!data) return [];

  return Array.from({ length: MEASUREMENT_LIMIT }, (_, i) => {
    const label = data[`PM_M${i + 1}`] || "";

    return {
      index: i,
      label: String(label),
    };
  }).filter((field) => field.label.trim() !== "");
};

export default function CustomerDetail({
  customerId,
  initialTab = "info",
}) {
  const { navigate, back } = useRouter();

  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);

  const [products, setProducts] = useState([]);
  const [expanded, setExpanded] = useState(null);

  // Measurement details stored separately for each product
  const [measByProduct, setMeasByProduct] = useState({});

  const [modalProduct, setModalProduct] = useState(null);

  // --------------------------------------------------
  // 1. GET LEDGER DETAILS
  // Called when customer page opens
  // --------------------------------------------------

  const load = async () => {
    setLoading(true);

    try {
      const res = await customersAPI.get(customerId);

      console.log("[Customer] Ledger Details:", res);

      setCustomer(res?.customer || null);
      setProducts(res?.products || []);
      setOrders([]);

      setMeasByProduct({});
      setExpanded(null);
    } catch (error) {
      console.error("[Customer] Load error:", error);
      setCustomer(null);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [customerId]);

  // --------------------------------------------------
  // 2. GET MEASUREMENT DETAILS
  // Called only when a product dropdown is expanded
  // --------------------------------------------------

  const loadMeasurement = async (productId, forceRefresh = false) => {
    const current = measByProduct[productId];

    if (
      !forceRefresh &&
      (current?.status === "loading" || current?.status === "loaded")
    ) {
      return current?.data || null;
    }

    setMeasByProduct((prev) => ({
      ...prev,
      [productId]: {
        status: "loading",
        data: null,
      },
    }));

    try {
      const cmId = getCmId();

      // Exact API payload:
      // {
      //   LMM_CM_ID: 10,
      //   LMM_LM_ID: 11,
      //   LMM_PM_ID: 41
      // }

      console.log("[Measurement] Request:", {
        LMM_CM_ID: cmId,
        LMM_LM_ID: customerId,
        LMM_PM_ID: productId,
      });

      const res = await measurementAPI.get(
        cmId,
        customerId,
        productId
      );

      console.log("[Measurement] Response:", res);

      if (res?.status !== "success" || !res?.data) {
        throw new Error(
          res?.message || "Measurement details not found"
        );
      }

      // IMPORTANT:
      // Store complete response even when LMM_ID is null.
      // PM_M1...PM_M12 contain the configured labels.

      const measurementData = res.data;

      setMeasByProduct((prev) => ({
        ...prev,
        [productId]: {
          status: "loaded",
          data: measurementData,
        },
      }));

      return measurementData;
    } catch (error) {
      console.error("[Measurement] Load error:", error);

      setMeasByProduct((prev) => ({
        ...prev,
        [productId]: {
          status: "error",
          data: null,
        },
      }));

      return null;
    }
  };

  // --------------------------------------------------
  // 3. PRODUCT DROPDOWN
  // --------------------------------------------------

  const toggleExpand = async (product) => {
    const productId = product.PM_ID;

    if (expanded === productId) {
      setExpanded(null);
      return;
    }

    setExpanded(productId);

    // Call second API only when required
    const current = measByProduct[productId];

    if (!current || current.status === "error") {
      await loadMeasurement(productId);
    }
  };

  // --------------------------------------------------
  // 4. OPEN ADD / EDIT MODAL
  // --------------------------------------------------

  const openMeasurementModal = (product, measurementData) => {
    if (!measurementData) {
      return;
    }

    // Combine product details and measurement API response.
    // This gives the modal both PM labels and LMM values.

    const completeProduct = {
      ...product,
      ...measurementData,
    };

    setModalProduct(completeProduct);
  };

  // --------------------------------------------------
  // 5. REFRESH AFTER SAVING
  // --------------------------------------------------

  const handleMeasurementSaved = async () => {
    if (!modalProduct) return;

    await loadMeasurement(modalProduct.PM_ID, true);
  };

  // --------------------------------------------------
  // LOADING
  // --------------------------------------------------

  if (loading) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="page">
        <div className="empty-state">
          <div className="empty-icon">❓</div>
          <div className="empty-title">
            Customer not found
          </div>
        </div>
      </div>
    );
  }

  // --------------------------------------------------
  // PAGE UI
  // --------------------------------------------------

  return (
    <div className="page">

      <Breadcrumb
        items={[
          {
            label: "Customers",
            onClick: () => navigate("customers"),
          },
          {
            label: customer.name,
          },
        ]}
      />

      {/* PAGE HEADER */}

      <div className="page-header">

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 50,
          }}
        >
          <button
              className="btn btn-ghost"
              onClick={() =>
                navigate("customers", {}, { replace: true })
              }
              style={{ color: "var(--gold)" }}
            >
              ←
          </button>

          <div>
            <h1 className="page-title">
              {customer.name}
            </h1>

            <p className="page-sub">
              {customer.phone} · Joined {fmt(customer.createdAt)}
            </p>
          </div>
        </div>

        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={() =>
              navigate("customer-edit", { id: customerId })
            }
          >
            ✏️ Edit
          </button>
        </div>

      </div>

      <div className="card">

        {/* TABS */}

        <div className="section-tabs">
          {[
            ["info", "👤 Information"],
            ["measurements", "📏 Measurements"],
          ].map(([t, label]) => (
            <div
              key={t}
              className={`section-tab${tab === t ? " active" : ""}`}
              onClick={() => setTab(t)}
            >
              {label}
            </div>
          ))}
        </div>

        {/* ------------------------------------------ */}
        {/* INFORMATION TAB */}
        {/* ------------------------------------------ */}

        {tab === "info" && (
          <div>

            <div
              className="detail-grid"
              style={{ marginBottom: 20 }}
            >

              <div className="detail-item">
                <label>Full Name</label>
                <span>{customer.name}</span>
              </div>

              <div className="detail-item">
                <label>Phone Number</label>
                <span>{customer.phone}</span>
              </div>

              <div
                className="detail-item"
                style={{ gridColumn: "1/-1" }}
              >
                <label>Address</label>
                <span>{customer.address || "—"}</span>
              </div>

              <div
                className="detail-item"
                style={{ gridColumn: "1/-1" }}
              >
                <label>Notes</label>
                <span style={{ color: "var(--text-muted)" }}>
                  {customer.notes || "—"}
                </span>
              </div>

              <div className="detail-item">
                <label>Member Since</label>
                <span>{fmt(customer.createdAt)}</span>
              </div>

              <div className="detail-item">
                <label>Total Orders</label>
                <span className="detail-value-gold">
                  {orders.length}
                </span>
              </div>

            </div>

            {customer.notes && (
              <div className="info-banner">
                <span>📝</span> {customer.notes}
              </div>
            )}

          </div>
        )}

        {/* ------------------------------------------ */}
        {/* MEASUREMENTS TAB */}
        {/* ------------------------------------------ */}

        {tab === "measurements" && (
          <div>

            <div
              className="form-section-header"
              style={{ marginBottom: 14 }}
            >
              📏 Measurements by Product
            </div>

            {products.length === 0 ? (

              <div className="empty-state">
                <div className="empty-icon">📏</div>

                <div className="empty-title">
                  No stitch types configured
                </div>

                <div className="empty-sub">
                  Add stitch types in Settings to start tracking measurements.
                </div>
              </div>

            ) : (

              products.map((p) => {

                const productId = p.PM_ID;

                const isOpen = expanded === productId;

                const meas = measByProduct[productId];

                // IMPORTANT:
                // Read labels from API 2, not API 1.

                const measurementData = meas?.data;

                const fields = getMeasurementLabels(
                  measurementData
                );

                // Null LMM_ID means no saved measurement.
                // It does NOT mean the product has no fields.

                const hasSavedMeasurement =
                  Number(measurementData?.LMM_ID) > 0;

                return (

                  <div
                    key={productId}
                    style={{
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      marginBottom: 10,
                      overflow: "hidden",
                    }}
                  >

                    {/* PRODUCT DROPDOWN HEADER */}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 12px",
                        cursor: "pointer",
                        background: "var(--surface-2, var(--surface))",
                      }}
                      onClick={() => toggleExpand(p)}
                    >

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >

                        <span>
                          {meas?.status === "loading"
                            ? "⏳"
                            : hasSavedMeasurement
                            ? "✅"
                            : "⬜"}
                        </span>

                        <span>{p.PM_Name}</span>

                      </div>

                      <span>
                        {isOpen ? "▼" : "▶"}
                      </span>

                    </div>

                    {/* PRODUCT MEASUREMENT BODY */}

                    {isOpen && (

                      <div style={{ padding: 12 }}>

                        {meas?.status === "loading" ? (

                          <div
                            style={{
                              padding: "10px 0",
                              textAlign: "center",
                            }}
                          >
                            <Spinner />
                          </div>

                        ) : meas?.status === "error" ? (

                          <div
                            style={{
                              color: "var(--red)",
                              fontSize: 13.5,
                            }}
                          >
                            Couldn't load measurement details.

                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ marginLeft: 10 }}
                              onClick={() =>
                                loadMeasurement(productId, true)
                              }
                            >
                              Retry
                            </button>
                          </div>

                        ) : !meas ? (

                          <div style={{ textAlign: "center" }}>
                            <Spinner />
                          </div>

                        ) : fields.length === 0 ? (

                          <div
                            style={{
                              color: "var(--text-dim)",
                              fontSize: 13.5,
                            }}
                          >
                            No measurement fields configured for this product.
                          </div>

                        ) : hasSavedMeasurement ? (

                          /* EXISTING SAVED MEASUREMENT */

                          <div>

                            <div
                              className="detail-grid"
                              style={{ marginBottom: 12 }}
                            >

                              {fields.map(({ index, label }) => (

                                <div
                                  className="detail-item"
                                  key={index}
                                >

                                  <label>{label}</label>

                                  <span>
                                    {measurementData[`LMM_M${index + 1}`] ?? "—"}
                                  </span>

                                </div>

                              ))}

                            </div>

                            {measurementData.LMM_Remarks && (

                              <div
                                className="info-banner"
                                style={{ marginBottom: 12 }}
                              >
                                <span>📝</span>
                                {measurementData.LMM_Remarks}
                              </div>

                            )}

                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() =>
                                openMeasurementModal(p, measurementData)
                              }
                            >
                              ✏️ Edit Measurement
                            </button>

                          </div>

                        ) : (

                          /* NEW MEASUREMENT */

                          <div>

                            <div
                              style={{
                                color: "var(--text-dim)",
                                marginBottom: 12,
                                fontSize: 13.5,
                              }}
                            >
                              No measurement saved yet for this stitch type.
                            </div>

                            <div
                              className="detail-grid"
                              style={{ marginBottom: 12 }}
                            >

                              {fields.map(({ index, label }) => (

                                <div
                                  className="detail-item"
                                  key={index}
                                >
                                  <label>{label}</label>
                                  <span>—</span>
                                </div>

                              ))}

                            </div>

                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() =>
                                openMeasurementModal(p, measurementData)
                              }
                            >
                              ＋ Add Measurement
                            </button>

                          </div>

                        )}

                      </div>

                    )}

                  </div>

                );
              })

            )}

          </div>
        )}

      </div>

      {/* MEASUREMENT MODAL */}

      <MeasurementModal
        open={!!modalProduct}
        product={modalProduct}
        ledgerId={customerId}
        onClose={() => setModalProduct(null)}
        onSaved={handleMeasurementSaved}
      />

    </div>
  );
}
